import { calendarDateUtcRange, dateToView } from "helpers/date";
import { formatValue } from "helpers/reports";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getPaymentDiscipline } from "sources/books/settlementPayments";
import { PanelStatus } from "./sidePanel";
import PanelDatetime from "./panelDatetime";

/** Keep current debt visible even when the selected period has no completed payments. */
function DisciplineMetrics({ data, period }) {
    const metrics = [
        ["Завершённых оплат", formatValue(data.completedPaymentsCount, "number")],
        ["Доля оплат с просрочкой", `${formatValue(data.overduePaymentsShare * 100, "double")}%`],
        ["Средняя просрочка", `${formatValue(data.averageOverdueDays, "double")} дн.`],
        ["Максимальная просрочка", `${formatValue(data.maxOverdueDays, "number")} дн.`],
    ];
    return (
        <>
            <section className="settlements__discipline-debt mt-3">
                <div className="small text-muted mb-1">Текущая просроченная задолженность</div>
                <strong className="settlements__discipline-debt-value">
                    {formatValue(data.currentOverdueDebt, "double")}
                </strong>
                <div className="small text-muted mt-2">
                    Просроченных оснований:{" "}
                    <b className="text-body">{formatValue(data.currentOverdueBidsCount, "number")}</b>
                </div>
            </section>
            <section className="mt-4">
                <h6 className="mb-1">Завершённые оплаты</h6>
                {period && (
                    <div className="small text-muted mb-3">
                        За период {dateToView(period.from)} — {dateToView(period.to)}
                    </div>
                )}
                {data.completedPaymentsCount ? (
                    <dl className="settlements__discipline-metrics mb-0">
                        {metrics.map(([label, value]) => (
                            <div key={label}>
                                <dt className="small text-muted font-weight-normal">{label}</dt>
                                <dd className="font-weight-bold mb-0 mt-2">{value}</dd>
                            </div>
                        ))}
                    </dl>
                ) : (
                    <div className="settlements__empty">
                        <strong>Нет завершённых оплат</strong>
                        <p className="small text-muted mb-0 mt-2">
                            За выбранный период нет данных для анализа завершённых оплат. Можно выбрать другой период.
                        </p>
                    </div>
                )}
            </section>
        </>
    );
}

/** Historical analysis is loaded only when the user opens this panel. */
export default function PaymentDiscipline({ counterparty, source, revision }) {
    const [state, setState] = useState(() => ({
        from: moment().subtract(90, "days").startOf("day"),
        to: moment().endOf("day"),
        data: null,
        period: null,
        loading: true,
        error: "",
    }));
    const update = useCallback((patch) => setState((previous) => ({ ...previous, ...patch })), []);
    const requestIdRef = useRef(0);
    const mounted = useRef(false);
    const latest = useRef(null);
    const requestKey = JSON.stringify([counterparty.id, source, revision]);
    useLayoutEffect(() => {
        latest.current = { from: state.from, to: state.to, counterpartyId: counterparty.id, source, requestKey };
    });

    const load = useCallback(async () => {
        const current = latest.current;
        const { from, to } = current;
        const requestId = ++requestIdRef.current;
        const isCurrent = () =>
            mounted.current && requestIdRef.current === requestId && latest.current.requestKey === current.requestKey;
        if (!from || !to || !moment(from).isValid() || !moment(to).isValid() || moment(from).isAfter(to)) {
            update({ error: "Укажите корректный период анализа.", loading: false });
            return;
        }
        update({ loading: true, error: "" });
        try {
            const data = await getPaymentDiscipline({
                counterpartyId: current.counterpartyId,
                source: current.source,
                ...calendarDateUtcRange(from, to),
            });
            if (!isCurrent()) {
                return;
            }
            if (!data) {
                throw new Error("Не удалось загрузить платёжную дисциплину.");
            }
            update({
                data,
                period: { from: moment(from).format("YYYY-MM-DD"), to: moment(to).format("YYYY-MM-DD") },
                loading: false,
            });
        } catch (error) {
            if (isCurrent()) {
                update({ error: error.message, loading: false });
            }
        }
    }, [update]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            requestIdRef.current += 1;
        };
    }, []);
    useEffect(() => {
        load();
    }, [load, requestKey]);
    const changePeriod = ({ name, value }) => update({ [name]: value });

    const { from, to, data, period, loading, error } = state;
    return (
        <div aria-busy={loading}>
            <div className="mb-3">
                <div className="small text-muted mb-1">Контрагент</div>
                <div className="settlements__counterparty">{counterparty.name}</div>
            </div>
            <section className="settlements__discipline-period mb-3" aria-label="Период анализа">
                <h6 className="mb-3">Период анализа</h6>
                <div className="form-row">
                    {[
                        { name: "from", label: "Начало периода", value: from },
                        { name: "to", label: "Конец периода", value: to },
                    ].map(({ name, label, value }) => (
                        <div key={name} className="col-12 col-sm-6 mb-2">
                            <PanelDatetime
                                label={label}
                                bWrapper="form-group mb-0"
                                labelWrapper="d-block small text-muted mb-1"
                                wrapper="w-100"
                                inputProps={{ name, type: "date" }}
                                value={value}
                                onChange={changePeriod}
                            />
                        </div>
                    ))}
                </div>
                <div className="d-flex justify-content-end mt-1">
                    <button type="button" className="btn btn-sm btn-brand" onClick={load} disabled={loading}>
                        {loading ? "Рассчитываем…" : "Рассчитать"}
                    </button>
                </div>
            </section>
            <PanelStatus loading={loading} error={error} retry={load} />
            {data && <DisciplineMetrics data={data} period={period} />}
        </div>
    );
}
